/* eslint-disable @typescript-eslint/no-explicit-any -- U2000Input* form handlers */
import U2000InputText from "../../../../u2000Core/components/formControls/input/U2000InputText";
import U2000CheckBox from "../../../../u2000Core/components/formControls/checkbox/U2000CheckBox";
import U2000AutoCompleteInput from "../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { NS } from "../../../U3110";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { U3110TaxRateFormRow } from "../../../U3110Service";
import U3110TaxRateComponentsSection from "./U3110TaxRateComponentsSection";
import { useU3110FindCountriesHandlers, useU3110FindStatesHandlers } from "../../../U3110UseFindHandlers";
import { isValidId } from "../../../../u2000Core/tools/U2000IdUtils";

interface U3110TaxRateBodyProps {
  formStateHandlers: U2000FormStateHandlers<U3110TaxRateFormRow>;
  t: TFunction;
}

function U3110TaxRateBody({ formStateHandlers, t }: U3110TaxRateBodyProps) {
  const { i18n } = useTranslation();
  const setProvinceSelection = useRef<((item: any) => void) | null>(null);
  const setProvinceInputValue = useRef<((value: string) => void) | null>(null);
  const previousCountryIdRef = useRef<string | null>(null);

  const findCountriesHandlers = useU3110FindCountriesHandlers();
  const countryIdProvider = useCallback(() => {
    const cid = formStateHandlers.getData()?.country?.id;
    return cid && isValidId(String(cid)) ? String(cid) : null;
  }, [formStateHandlers]);
  const findStatesHandlers = useU3110FindStatesHandlers(countryIdProvider);

  const countryResultFields = useMemo(() => {
    const language = i18n.language;
    if (language === "fr") return ["countryNameFr"];
    if (language === "en") return ["countryNameEn"];
    return ["countryName"];
  }, [i18n.language]);

  const provinceResultFields = useMemo(() => {
    const language = i18n.language;
    if (language === "fr") return ["stateNameFr"];
    if (language === "en") return ["stateNameEn"];
    return ["stateName"];
  }, [i18n.language]);

  const onSearchCountry = useCallback(
    (criterias: { fieldName: string; value: any }[]) => findCountriesHandlers.search(criterias),
    [findCountriesHandlers],
  );

  const displayCountry = (item: any) => {
    if (!item) return "";
    const language = i18n.language;
    if (language === "fr" && item.countryNameFr) return item.countryNameFr;
    if (language === "en" && item.countryNameEn) return item.countryNameEn;
    return item.countryName || item.name || "";
  };

  const extractCountryData = (result: any) => {
    if (result) {
      const language = i18n.language;
      let countryName = "";
      if (language === "fr" && result.countryNameFr) countryName = result.countryNameFr;
      else if (language === "en" && result.countryNameEn) countryName = result.countryNameEn;
      else countryName = result.countryName || result.name || "";
      return [
        { fieldName: "country.id", value: result.id },
        { fieldName: "country.name", value: countryName },
        { fieldName: "country.countryName", value: countryName },
        { fieldName: "country.countryNameFr", value: result.countryNameFr || "" },
        { fieldName: "country.countryNameEn", value: result.countryNameEn || "" },
      ];
    }
    return [
      { fieldName: "country.id", value: null },
      { fieldName: "country.name", value: null },
      { fieldName: "country.countryName", value: null },
      { fieldName: "country.countryNameFr", value: null },
      { fieldName: "country.countryNameEn", value: null },
    ];
  };

  const onSearchProvince = useCallback(
    (criterias: { fieldName: string; value: any }[]) => findStatesHandlers.search(criterias),
    [findStatesHandlers],
  );

  const displayProvince = (item: any) => {
    if (!item) return "";
    const language = i18n.language;
    if (language === "fr" && item.stateNameFr) return item.stateNameFr;
    if (language === "en" && item.stateNameEn) return item.stateNameEn;
    return item.stateName || item.name || "";
  };

  const extractProvinceData = (result: any) => {
    if (result) {
      const language = i18n.language;
      let stateName = "";
      if (language === "fr" && result.stateNameFr) stateName = result.stateNameFr;
      else if (language === "en" && result.stateNameEn) stateName = result.stateNameEn;
      else stateName = result.stateName || result.name || "";
      return [
        { fieldName: "province.id", value: result.id },
        { fieldName: "province.name", value: stateName },
        { fieldName: "province.stateName", value: stateName },
        { fieldName: "province.stateNameFr", value: result.stateNameFr || "" },
        { fieldName: "province.stateNameEn", value: result.stateNameEn || "" },
      ];
    }
    return [
      { fieldName: "province.id", value: null },
      { fieldName: "province.name", value: null },
      { fieldName: "province.stateName", value: null },
      { fieldName: "province.stateNameFr", value: null },
      { fieldName: "province.stateNameEn", value: null },
    ];
  };

  useEffect(() => {
    const data = formStateHandlers.getData();
    const countryId = data?.country?.id || null;
    const currentCountryId = countryId && isValidId(countryId) ? countryId : null;
    const previousCountryId = previousCountryIdRef.current;
    const countryChanged = previousCountryId !== null && previousCountryId !== currentCountryId;
    const countryCleared = previousCountryId !== null && currentCountryId === null;
    const pid = data?.province?.id;
    if ((countryChanged || countryCleared) && pid != null && isValidId(String(pid))) {
      const setNestedValue = (obj: any, path: string, value: any): any => {
        const keys = path.split(".");
        const cloned = JSON.parse(JSON.stringify(obj));
        let current = cloned;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) current[keys[i]] = {};
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        return cloned;
      };
      let next = setNestedValue(data, "province.id", null);
      next = setNestedValue(next, "province.name", null);
      next = setNestedValue(next, "province.stateName", null);
      next = setNestedValue(next, "province.stateNameFr", null);
      next = setNestedValue(next, "province.stateNameEn", null);
      formStateHandlers.setData(next);
      setProvinceSelection.current?.(null);
      setProvinceInputValue.current?.("");
    }
    if (previousCountryId === null || previousCountryId !== currentCountryId) {
      previousCountryIdRef.current = currentCountryId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formStateHandlers.getData()?.country?.id]);

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <h3 className="text-base font-semibold text-gray-900">{t("lblSectionRegime", { ns: NS })}</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <U2000InputText formStateHandlers={formStateHandlers as any} fieldName="code" label={t("lblCode", { ns: NS })} required />
          <div className="space-y-1.5">
            <span aria-hidden="true" className="mb-1.5 block text-sm font-medium text-transparent">
              &nbsp;
            </span>
            <div className="flex h-[46px] items-center">
              <U2000CheckBox formStateHandlers={formStateHandlers as any} fieldName="active" label={t("lblActive", { ns: NS })} />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <U2000AutoCompleteInput
            formStateHandlers={formStateHandlers as any}
            fieldName="country"
            mainFieldName="countryName"
            label={t("lblCountry", { ns: NS })}
            onSearch={onSearchCountry}
            displayFn={displayCountry}
            overrideExtractDataFn={extractCountryData}
            minCarLaunchSearch={0}
            resultLabels={[]}
            resultFields={countryResultFields}
            required={false}
          />
          <U2000AutoCompleteInput
            formStateHandlers={formStateHandlers as any}
            fieldName="province"
            mainFieldName="stateName"
            label={t("lblProvince", { ns: NS })}
            onSearch={onSearchProvince}
            displayFn={displayProvince}
            overrideExtractDataFn={extractProvinceData}
            minCarLaunchSearch={0}
            resultLabels={[]}
            resultFields={provinceResultFields}
            readonly={!formStateHandlers.getData()?.country?.id || formStateHandlers.getData()?.country?.id === ""}
            required={false}
            onSetCurrentSelection={(setCurrentSelection, setInputValue) => {
              setProvinceSelection.current = setCurrentSelection;
              setProvinceInputValue.current = setInputValue;
            }}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <U2000InputText formStateHandlers={formStateHandlers as any} fieldName="labelFr" label={t("lblLabelFr", { ns: NS })} />
          <U2000InputText formStateHandlers={formStateHandlers as any} fieldName="labelEn" label={t("lblLabelEn", { ns: NS })} />
        </div>
      </div>

      <U3110TaxRateComponentsSection formStateHandlers={formStateHandlers} t={t} />
    </div>
  );
}

export default U3110TaxRateBody;
